import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Calendar, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import ProductIdentifierCombobox, { type SelectedProduct } from "./ProductIdentifierCombobox";
import NegativeBalanceConfirmDialog from "./NegativeBalanceConfirmDialog";
import type { PoolType } from "@/hooks/useProductPools";
import { useRecordInventoryAdjustment } from "@/hooks/useRecordInventoryAdjustment";
import { useInventoryLedgerSources } from "@/hooks/useInventoryLedgerSources";
import { poolTypeFromOriginKind } from "@/shared/types/inventorySource";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface Props {
  organisationId: string;
  onSuccess?: () => void;
}

const POOL_LABEL: Record<PoolType, string> = {
  received: "Received pool",
  platform: "Platform pool",
};

interface PreviewState {
  current: number;
  wouldBe: number;
  delta: number;
  poolType: PoolType;
  source: string;
}

const ManualAdjustmentForm: React.FC<Props> = ({ organisationId, onSuccess }) => {
  const queryClient = useQueryClient();
  const [product, setProduct] = useState<SelectedProduct | null>(null);
  const [quantity, setQuantity] = useState<string>("");
  const [date, setDate] = useState<Date>(new Date());
  const [note, setNote] = useState("");
  const [pendingPreview, setPendingPreview] = useState<PreviewState | null>(null);
  const [ledgerSource, setLedgerSource] = useState<string>("");

  const { data: sources = [], isLoading: sourcesLoading } = useInventoryLedgerSources(organisationId, true);

  const selectableSources = useMemo(
    () => sources.filter((s) => s.source !== "manual_received"),
    [sources],
  );

  useEffect(() => {
    if (selectableSources.length === 0) return;
    if (!ledgerSource || ledgerSource === "manual_received") {
      setLedgerSource(selectableSources[0].source);
    }
  }, [selectableSources, ledgerSource]);

  const selectedMeta = useMemo(
    () => selectableSources.find((s) => s.source === ledgerSource),
    [selectableSources, ledgerSource],
  );

  const derivedPoolType: PoolType = useMemo(() => {
    if (!selectedMeta) return "received";
    return poolTypeFromOriginKind(selectedMeta.origin_kind);
  }, [selectedMeta]);

  const { preview, confirm } = useRecordInventoryAdjustment();

  const parsedQuantity = useMemo(() => {
    const n = Number(quantity);
    if (!Number.isFinite(n)) return NaN;
    if (!Number.isInteger(n)) return NaN;
    return n;
  }, [quantity]);

  const canSubmit =
    !!product &&
    !!ledgerSource &&
    Number.isInteger(parsedQuantity) &&
    parsedQuantity !== 0 &&
    !preview.isPending &&
    !confirm.isPending;

  const reset = () => {
    setProduct(null);
    setQuantity("");
    setDate(new Date());
    setNote("");
    setPendingPreview(null);
  };

  const invalidateAfterWrite = () => {
    queryClient.invalidateQueries({ queryKey: ["product-pools", organisationId] });
    queryClient.invalidateQueries({ queryKey: ["inventory-audit-log", organisationId] });
    queryClient.invalidateQueries({ queryKey: ["adjustment-deltas", organisationId] });
    queryClient.invalidateQueries({ queryKey: ["inventory-ledger-sources", organisationId] });
    queryClient.invalidateQueries({ queryKey: ["manual-adjustment-current-stock", organisationId] });
  };

  const submit = async () => {
    if (!canSubmit || !product) return;
    try {
      // UC-163 Work Item 4: Support adjustment by identifier for unlinked identifiers
      const adjustmentPayload: Parameters<typeof preview.mutateAsync>[0] = {
        organisation_id: organisationId,
        source: ledgerSource,
        quantity: parsedQuantity,
        effective_date: format(date, "yyyy-MM-dd"),
        note: note.trim() || undefined,
      };

      if (product.master_product_id) {
        adjustmentPayload.master_product_id = product.master_product_id;
      } else if (product.identifier_type && product.identifier_value) {
        adjustmentPayload.identifier_type = product.identifier_type;
        adjustmentPayload.identifier_value = product.identifier_value;
      } else {
        throw new Error("Product must have either master_product_id or identifier type/value");
      }

      const result = await preview.mutateAsync(adjustmentPayload);
      if (result.preview === true) {
        setPendingPreview({
          current: result.current_balance,
          wouldBe: result.would_be_balance,
          delta: result.delta,
          poolType: result.pool_type,
          source: result.source ?? ledgerSource,
        });
        return;
      }
      const transientNote = result.transient ? " (transient on live source — next sync replaces it)" : "";
      toast.success(
        `Adjustment recorded: ${result.before_quantity} → ${result.after_quantity} (${POOL_LABEL[result.pool_type]})${transientNote}`,
      );
      invalidateAfterWrite();
      reset();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to record adjustment";
      toast.error(message);
    }
  };

  const confirmNegative = async () => {
    if (!product || !pendingPreview) return;
    try {
      // UC-163 Work Item 4: Support adjustment by identifier for unlinked identifiers
      const confirmPayload: Parameters<typeof confirm.mutateAsync>[0] = {
        organisation_id: organisationId,
        source: pendingPreview.source,
        quantity: pendingPreview.delta,
        effective_date: format(date, "yyyy-MM-dd"),
        note: note.trim() || undefined,
      };

      if (product.master_product_id) {
        confirmPayload.master_product_id = product.master_product_id;
      } else if (product.identifier_type && product.identifier_value) {
        confirmPayload.identifier_type = product.identifier_type;
        confirmPayload.identifier_value = product.identifier_value;
      } else {
        throw new Error("Product must have either master_product_id or identifier type/value");
      }

      const result = await confirm.mutateAsync(confirmPayload);
      if (result.preview === false) {
        const transientNote = result.transient ? " (transient on live source — next sync replaces it)" : "";
        toast.success(
          `Adjustment recorded: ${result.before_quantity} → ${result.after_quantity} (${POOL_LABEL[result.pool_type]})${transientNote}`,
        );
        invalidateAfterWrite();
        onSuccess?.();
        reset();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to record adjustment";
      toast.error(message);
    }
  };

  const liveWarning = selectedMeta?.origin_kind === "live";

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Manual Inventory Adjustment</CardTitle>
          <CardDescription>
            Enter a positive number to add stock, or a negative number to remove it.
            All adjustments are logged and can&apos;t be deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Inventory source</Label>
            {sourcesLoading ? (
              <p className="text-sm text-muted-foreground">Loading sources…</p>
            ) : selectableSources.length === 0 ? (
              <p className="text-sm text-muted-foreground">No inventory sources registered yet.</p>
            ) : (
              <Select value={ledgerSource} onValueChange={setLedgerSource}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {selectableSources.map((s) => (
                    <SelectItem key={s.source} value={s.source}>
                      {s.display_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {liveWarning && (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-amber-800 border-amber-300 bg-amber-50">
                  Live source
                </Badge>
                <span className="text-xs text-muted-foreground">
                  This adjustment applies until the next live sync; then the platform snapshot wins.
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Product</Label>
            <ProductIdentifierCombobox
              organisationId={organisationId}
              value={product}
              onSelect={setProduct}
            />
          </div>

          <div className="space-y-2">
            <Label>Quantity</Label>
            <Input
              type="number"
              inputMode="numeric"
              step={1}
              placeholder="e.g. -5 or 12"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            {quantity !== "" && (Number.isNaN(parsedQuantity) || parsedQuantity === 0) && (
              <p className="text-xs text-red-600">Enter a non-zero whole number.</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Effective date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                placeholder="Reason for adjustment"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={submit} disabled={!canSubmit}>
              {(preview.isPending || confirm.isPending) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recording…
                </>
              ) : (
                "Confirm adjustment"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <NegativeBalanceConfirmDialog
        open={!!pendingPreview}
        poolType={pendingPreview?.poolType ?? derivedPoolType}
        currentBalance={pendingPreview?.current ?? 0}
        wouldBeBalance={pendingPreview?.wouldBe ?? 0}
        delta={pendingPreview?.delta ?? 0}
        isConfirming={confirm.isPending}
        onCancel={() => setPendingPreview(null)}
        onProceed={confirmNegative}
      />
    </>
  );
};

export default ManualAdjustmentForm;
